import { parseAnswer } from "./ContractSearchHelper";
import { MatchedContract } from "./ContractSearchTypes";

/** A name compared loosely: case, runs of spaces and a trailing colon do not matter. */
const normalise = (text: string): string =>
  text.replace(/\s+/g, " ").trim().replace(/:$/, "").toLowerCase();

const withoutExtension = (name: string): string => name.replace(/\.[a-z0-9]{1,5}$/i, "");

/** The contracts a normalised heading names: those with exactly that name, else those with it but for the extension. */
const namedBy = (contracts: MatchedContract[], wanted: string): MatchedContract[] => {
  const exact = contracts.filter((contract) => normalise(contract.fileName) === wanted);
  if (exact.length) return exact;
  return contracts.filter(
    (contract) => withoutExtension(normalise(contract.fileName)) === withoutExtension(wanted),
  );
};

/**
 * The matched contract an answer heading names, or `null`. The AI writes one bold heading per contract (the frames
 * draw the compare pill beside it), usually the file name; one written without its extension still matches.
 *
 * A heading that fits **more than one** matched contract (the same file name twice, or `MSA` for `MSA.pdf` and
 * `MSA.docx`) names none of them: a pill beside it would open one of them silently, and the user would compare
 * against the wrong old version. Those contracts keep the control on their own rows, where the meta line tells them
 * apart (D59).
 */
export const contractForHeading = (
  contracts: MatchedContract[],
  heading: string,
): MatchedContract | null => {
  const wanted = normalise(heading);
  if (!wanted) return null;
  const found = namedBy(contracts, wanted);
  return found.length === 1 ? found[0] : null;
};

/**
 * The ids of the matched contracts that some heading of the answer names. Those carry the compare control in the
 * answer text; every other matched contract keeps it beside its row, so none is left without a way in.
 */
export const contractsNamedInHeadings = (
  answer: string,
  contracts: MatchedContract[],
): Set<string> => {
  const named = new Set<string>();
  parseAnswer(answer).forEach((block) => {
    if (block.kind !== "heading") return;
    const contract = contractForHeading(contracts, block.runs.map((run) => run.text).join(""));
    if (contract) named.add(contract.documentId);
  });
  return named;
};
